import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { isEmptyDescription, normalizeTitle, normalizeDescription } from "@/utils/contentUtils";

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<boolean> | boolean;
  isHeading?: boolean;
  isMultiline?: boolean;
  placeholder?: string;
  isLoading?: boolean;
}

const EditableText = ({
  value,
  onChange,
  onSave,
  isHeading = false,
  isMultiline = false,
  placeholder = "Add text...",
  isLoading = false
}: EditableTextProps) => {
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(isHeading ? normalizeTitle(value) : normalizeDescription(value));
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const originalValueRef = useRef(isHeading ? normalizeTitle(value) : normalizeDescription(value));
  const isSavingRef = useRef(false);
  
  // Update local value when value prop changes and we're not editing
  useEffect(() => {
    if (!editing && !isSavingRef.current) {
      console.log("[EditableText] Received new value:", value);
      // Always normalize the incoming value for consistent display
      const normalizedValue = isHeading ? normalizeTitle(value) : normalizeDescription(value);
      setLocalValue(normalizedValue);
      originalValueRef.current = normalizedValue;
    }
  }, [value, editing, isHeading]);

  useEffect(() => {
    // Auto-focus the input when editing begins
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleDoubleClick = () => {
    if (isLoading) return;
    
    console.log("[EditableText] Double-clicked, starting edit mode with value:", value);
    const normalizedValue = isHeading ? normalizeTitle(value) : normalizeDescription(value);
    setEditing(true);
    setLocalValue(normalizedValue); // Reset local value to current normalized value when starting to edit
    originalValueRef.current = normalizedValue;
  };

  const handleChange = (newValue: string) => {
    console.log("[EditableText] Value changed to:", newValue);
    setLocalValue(newValue); // Update local state
    onChange(newValue); // Propagate the change to parent component
    // Remove any auto-resize logic that could interfere with text selection
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only handle the specific keys we care about, let everything else pass through
    if (isMultiline) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        saveChanges();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
      // Let all other keys (including Cmd+A) pass through naturally
    } else {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveChanges();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
      // Let all other keys (including Cmd+A) pass through naturally
    }
  };

  const saveChanges = async () => {
    if (isLoading) return;
    
    console.log("[EditableText] Saving changes. Previous:", originalValueRef.current, "New:", localValue);
    setEditing(false);
    
    // Only save if the value has actually changed
    if (localValue !== originalValueRef.current) {
      isSavingRef.current = true;
      try {
        const result = await onSave();
        
        if (result === false) {
          // If save failed, revert to the original value
          console.log("[EditableText] Save failed, reverting to original value:", originalValueRef.current);
          setLocalValue(originalValueRef.current);
          onChange(originalValueRef.current);
        } else {
          // Update our reference to the new value
          console.log("[EditableText] Save successful, updating reference to:", localValue);
          originalValueRef.current = localValue;
        }
      } catch (error) {
        console.error("[EditableText] Error during save:", error);
        // If an error occurred, revert to the original value
        setLocalValue(originalValueRef.current);
        onChange(originalValueRef.current);
      } finally {
        isSavingRef.current = false;
      }
    }
  };

  const cancelEdit = () => {
    console.log("[EditableText] Cancelling edit, resetting to:", originalValueRef.current);
    setLocalValue(originalValueRef.current); // Reset to original value
    onChange(originalValueRef.current); // Propagate the reset to parent component
    setEditing(false);
  };

  const handleBlur = async () => {
    // Don't do anything if we're loading
    if (isLoading) return;
    
    // Only save if the value has actually changed
    console.log("[EditableText] Input blurred. Current:", localValue, "Original:", originalValueRef.current);
    if (localValue !== originalValueRef.current) {
      await saveChanges();
    } else {
      setEditing(false);
    }
  };

  if (editing) {
    if (isMultiline) {
      return (
        <Textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="text-clustr-darkGray text-sm resize-none"
          disabled={isLoading}
          data-testid="editable-textarea"
        />
      );
    }
    
    return (
      <Input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={cn(
          "h-auto py-1",
          isHeading && "text-[24px] !font-semibold"
        )}
        disabled={isLoading}
        data-testid="editable-input"
      />
    );
  }
  
  // For non-editing mode, ensure we're displaying the normalized value
  const displayValue = isHeading ? normalizeTitle(localValue) : normalizeDescription(localValue);
  
  if (isHeading) {
    return (
      <h1 
        className={cn(
          "cursor-pointer hover:bg-gray-100 px-2 -ml-2 rounded",
          "text-[24px] !font-semibold"
        )}
        onDoubleClick={handleDoubleClick}
        title="Double-click to edit"
        data-testid="editable-heading"
      >
        {displayValue || placeholder}
      </h1>
    );
  }
  
  // Use our utility to check for empty description
  if (isEmptyDescription(displayValue)) {
    return (
      <p 
        className="text-clustr-darkGray text-sm italic cursor-pointer hover:bg-gray-100 px-2 -ml-2 rounded"
        onDoubleClick={handleDoubleClick}
        title="Double-click to add description"
        data-testid="editable-empty-description"
      >
        {placeholder}
      </p>
    );
  }
  
  return (
    <p 
      className="text-clustr-darkGray text-sm cursor-pointer hover:bg-gray-100 px-2 -ml-2 rounded"
      onDoubleClick={handleDoubleClick}
      title="Double-click to edit"
      data-testid="editable-description"
    >
      {displayValue}
    </p>
  );
};

export default EditableText;
